// ─── Spanish translations for Adventure Track! ───
// Terminology guide (consistent across the app):
//   Library → Biblioteca       Planned → Pendiente
//   Dashboard → Panel          In Progress → En curso
//   Search → Buscar            Completed → Completado
//   Profile → Perfil           Dropped → Abandonado
//   Achievements → Logros      Rating → Calificación
//   Challenges → Desafíos      Review → Reseña

const es = {
  // ── Common / Shared ──
  common: {
    loading: "Cargando...",
    save: "Guardar",
    saving: "Guardando...",
    cancel: "Cancelar",
    back: "Volver",
    viewAll: "Ver todo",
    seeMore: "Ver más",
    refresh: "Actualizar",
    search: "Buscar",
    delete: "Eliminar",
    edit: "Editar",
    remove: "Eliminar",
    create: "Crear",
    close: "Cerrar",
    authenticating: "Autenticando...",
    browsMedia: "Explorar contenido",
  },

  // ── Media types ──
  mediaTypes: {
    book: "Libro",
    movie: "Película",
    tv_series: "Serie de TV",
    video_game: "Videojuego",
    manga: "Manga",
    comic: "Cómic",
    graphic_novel: "Novela gráfica",
  },
  mediaTypesPlural: {
    book: "Libros",
    movie: "Películas",
    tv_series: "Series",
    video_game: "Videojuegos",
    manga: "Manga",
    comic: "Cómics",
    graphic_novel: "Novelas gráficas",
  },
  mediaTypesShort: {
    book: "Libro",
    movie: "Película",
    tv_series: "TV",
    video_game: "Juego",
    manga: "Manga",
    comic: "Cómic",
    graphic_novel: "NG",
  },

  // ── Statuses ──
  statuses: {
    planned: "Pendiente",
    in_progress: "En curso",
    completed: "Completado",
    dropped: "Abandonado",
  },
  statusesShort: {
    planned: "Pendiente",
    in_progress: "En curso",
    completed: "Listo",
    dropped: "Abandonado",
  },

  // ── Navigation ──
  nav: {
    dashboard: "Tu aventura",
    library: "Biblioteca",
    search: "Buscar",
    whatsNext: "¿Qué sigue?",
    achievements: "Logros",
    challenges: "Desafíos",
    profile: "Perfil",
    home: "Inicio",
    discover: "Actividad",
    lightMode: "Modo claro",
    darkMode: "Modo oscuro",
    logout: "Cerrar sesión",
    loggedOut: "Sesión cerrada correctamente",
  },

  // ── Landing page ──
  landing: {
    signInGoogle: "Iniciar sesión con Google",
    signUpGoogle: "Regístrate con Google",
    heroTitle1: "Descubre.",
    heroTitle2: "Conecta.",
    heroTitle3: "Comparte.",
    heroSubtitle1: "Todo tu contenido, en un solo lugar.",
    heroSubtitle2: "Y todas las historias, conectadas.",
    startAdventure: "Comienza tu aventura",
    trackAllMedia: "Todo conectado en un mismo lugar",
    readyToStart: "Todo empieza con una historia",
    joinToday: "Únete a Adventure Track! Una aventura es más divertida si se comparte.",
    trackJourney: "Organiza tu viaje multimedia.",
    featuresCommon: "Registra, organiza y sigue todo tu contenido sin necesidad de cambiar de plataforma",
    features: {
      books: { title: "Libros" },
      movies: { title: "Películas" },
      tvSeries: { title: "Series" },
      videoGames: { title: "Videojuegos" },
    },
    stats: {
      rate: { value: "Tu valoración suma", label: "Puntúa cada historia" },
      track: { value: "Cada paso cuenta", label: "Consigue nuevos logros" },
      review: { value: "Deja tu huella", label: "Comparte lo que piensas" },
    },
  },

  // ── Dashboard ──
  dashboard: {
    welcomeBack: "Hola,",
    overview: "Todo lo que forma parte de tu aventura",
    totalItems: "Total",
    inProgress: "En curso",
    completed: "Completados",
    avgRating: "Calificación media",
    quickActions: "Acciones rápidas",
    discover: "Descubrir",
    whatsNext: "¿Qué sigue?",
    activeGoals: "Metas activas",
    completionRate: "Tasa de finalización",
    itemsCompleted: (done, total) => `${done} de ${total} completados`,
    byMediaType: "Por tipo de contenido",
    currentlyInProgress: "En curso",
    recentlyAdded: "Agregados recientemente",
    viewLibrary: "Ver biblioteca",
    emptyTitle: "Tu biblioteca está vacía",
    emptyDescription: "Comienza a registrar tu contenido buscando y agregando títulos",
    loadError: "Error al cargar el panel",
    loadingDashboard: "Cargando panel...",
    planned: "Pendientes",
    dropped: "Abandonados",
    exploreSection: "Explora",
    connectSection: "Conecta",
    latestActivity: "Última actividad",
    achievementProgress: "Tu progreso en logros",
    youAreAt: (stage) => `Estás en ${stage}`,
    achievementOnTrack: (n) => `¡Vas por buen camino! Te quedan ${n} logro${n !== 1 ? "s" : ""} para completar esta etapa.`,
    achievementAllDone: "¡Felicidades! Has completado todos los logros de esta etapa.",
    collectionByType: "Tu colección por tipo",
  },

  // ── Library ──
  library: {
    title: "Mi Biblioteca",
    itemCount: (n) => `${n} ${n === 1 ? "título" : "títulos"} en tu colección`,
    addItem: "Agregar título",
    gridView: "Vista en cuadrícula",
    listView: "Vista en lista",
    allTypes: "Todos",
    allStatuses: "Todos los estados",
    allRatings: "Todas las calificaciones",
    unrated: "Sin calificar",
    recentlyAdded: "Agregados recientemente",
    recentlyUpdated: "Actualizados recientemente",
    rating: "Calificación",
    clearFilters: "Limpiar filtros",
    moreFilters: "Más filtros",
    sortByPriority: "Prioridad",
    priorityLabel: "Prioridad",
    dragHint: "Arrastra para reordenar",
    addTitle: "Agregar título",
    contentType: "Tipo de contenido",
    loadingLibrary: "Cargando biblioteca...",
    noMatch: "Ningún título coincide con los filtros",
    emptyLibrary: "Tu biblioteca está vacía",
    adjustFilters: "Intenta ajustar los filtros",
    startSearching: "Empieza buscando y agregando contenido",
    loadError: "Error al cargar la biblioteca",
  },

  // ── Search ──
  search: {
    title: "Buscar",
    subtitle: "Encuentra tu próxima aventura",
    placeholder: "Buscar títulos...",
    searchButton: "Buscar",
    searching: "Buscando...",
    resultsFor: (n, q) => `${n} resultados para "${q}"`,
    noResults: "Sin resultados",
    noResultsFor: (q) => `No se encontraron resultados para "${q}". Intenta otra búsqueda.`,
    createNewTitle: "Crear título nuevo",
    trendingThisWeek: "Tendencias de la semana",
    loadingTrending: "Cargando tendencias...",
    noTrending: "No hay contenido en tendencia disponible.",
    searchFailed: "Error en la búsqueda",
    allTypes: "Todos los tipos",
    popular: "Popular",
  },

  // ── Profile ──
  profile: {
    name: "Nombre",
    bio: "Biografía",
    bioPlaceholder: "Cuéntanos sobre ti...",
    anonymous: "Anónimo",
    profileUpdated: "Perfil actualizado",
    updateError: "Error al actualizar el perfil",
    total: "Total",
    done: "Listos",
    active: "Activos",
    rating: "Calificación",
    collectionBreakdown: "Desglose de la colección",
    myCollection: "Mi Colección",
    all: "Todo",
    noItemsYet: "Aún no hay títulos en tu biblioteca",
    noStatusItems: (s) => `No hay títulos ${s}`,
    viewAllItems: (n) => `Ver todo (${n} títulos)`,
    loadingProfile: "Cargando perfil...",
    avatarUpdated: "Foto de perfil actualizada",
    avatarError: "Error al subir la imagen",
    themeLight: "Claro",
    themeDark: "Oscuro",
    themeSystem: "Sistema",
    themeLabel: "Tema de la aplicación",
    preferences: "Preferencias",
  },

  // ── Achievements ──
  achievements: {
    title: "Logros",
    subtitle: "Sigue tu progreso y desbloquea insignias",
    yourProgress: "Tu progreso",
    unlocked: "Logros desbloqueados",
    moreToUnlock: (n) => `¡${n} más por desbloquear!`,
    unlockedSection: (n) => `Desbloqueados (${n})`,
    lockedSection: (n) => `Bloqueados (${n})`,
    noAchievements: "No hay logros disponibles",
    startAdding: "¡Agrega títulos a tu biblioteca para desbloquear logros!",
    earnedOn: (date) => `Obtenido el ${date}`,
  },

  // ── Challenges ──
  challenges: {
    title: "Desafíos mensuales",
    subtitle: "Establece metas y sigue tu progreso",
    newChallenge: "Nuevo desafío",
    createTitle: "Crear nuevo desafío",
    createDescription: "Establece una meta mensual para completar cierta cantidad de títulos.",
    challengeTitle: "Nombre del desafío",
    challengePlaceholder: "Ej: Leer 5 libros este mes",
    targetCount: "Cantidad meta",
    mediaTypeOptional: "Tipo de contenido (opcional)",
    allTypes: "Todos los tipos",
    month: "Mes",
    year: "Año",
    createChallenge: "Crear desafío",
    creating: "Creando...",
    progress: "Progreso",
    challengeCompleted: "¡Desafío completado!",
    moreToGo: (n) => `¡${n} más por completar!`,
    activeChallenges: (n) => `Desafíos activos (${n})`,
    noActive: "No hay desafíos activos. ¡Crea uno para empezar!",
    completedChallenges: (n) => `Desafíos completados (${n})`,
    created: "Desafío creado",
    deleted: "Desafío eliminado",
    titleRequired: "Ingresa un nombre para el desafío",
    createError: "Error al crear el desafío",
    deleteError: "Error al eliminar el desafío",
    months: [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ],
  },

  // ── What's Next (Recommendations) ──
  whatsNext: {
    title: "¿Qué sigue?",
    subtitle: "Recomendaciones personalizadas según tu biblioteca",
    startBuilding: "Empieza a construir tu biblioteca",
    startBuildingDesc: "¡Agrega títulos a tu biblioteca y te daremos recomendaciones personalizadas!",
    basedOnFavorites: "Basado en tus favoritos",
    popularIn: (type) => `Popular en ${type}`,
    trendingNow: "Tendencias ahora",
    trendingThisWeek: "Tendencia esta semana",
    fromCreators: "De creadores que te gustan",
    byCreator: (name) => `De ${name}`,
    highlyRated: "Mejor calificados",
    ratingLabel: (r) => `★ ${r} de calificación`,
    loadError: "Error al cargar recomendaciones",
  },

  // ── Media Detail ──
  mediaDetail: {
    editEntry: "Editar entrada",
    removeFromLibrary: "Eliminar",
    addToLibrary: "Agregar a biblioteca",
    votes: (n) => `(${n.toLocaleString()} votos)`,
    season: "Temporada",
    seasons: "Temporadas",
    episodes: "Episodios",
    overview: "Sinopsis",
    director: "Director",
    creator: "Creador",
    cast: "Reparto",
    yourNotes: "Tus notas",
    review: "Reseña",
    notes: "Notas",
    communityReviews: "Reseñas de la comunidad",
    notFound: "Título no encontrado",
    loadError: "Error al cargar el título",
    removed: "Eliminado de la biblioteca",
    removeError: "Error al eliminar",
    yourRating: "Tu calificación:",
    description: "Descripción",
    yourEntry: "Tu entrada",
    ratingCount: (n) => `(${n} ${n === 1 ? "calificación" : "calificaciones"})`,
    removedFromLibrary: "Eliminado de la biblioteca",
    removeFromLibraryError: "Error al eliminar de la biblioteca",
  },

  // ── Add to Library Dialog ──
  addToLibraryDialog: {
    editLibrary: "Editar en biblioteca",
    addToLibrary: "Agregar a biblioteca",
    status: "Estado",
    rating: "Calificación",
    reviewOptional: "Reseña (opcional)",
    reviewPlaceholder: "Comparte tu opinión...",
    notesOptional: "Notas personales (opcional)",
    notesPlaceholder: "Notas privadas para ti...",
    update: "Actualizar",
    addButton: "Agregar a biblioteca",
    updated: "Entrada actualizada",
    added: "Agregado a la biblioteca",
    saveError: "Error al guardar",
  },

  // ── Create Item Dialog ──
  createItem: {
    title: "Agregar nuevo título",
    subtitle: "Agregar un título manualmente a la base de datos",
    titleLabel: "Título *",
    titlePlaceholder: "Ingresa el título...",
    mediaType: "Tipo de contenido",
    releaseYear: "Año de lanzamiento",
    creator: "Creador / Autor / Desarrollador",
    creatorPlaceholder: "Ej: CD Projekt Red, Eiichiro Oda...",
    coverImage: "URL de portada (opcional)",
    description: "Descripción (opcional)",
    descriptionPlaceholder: "Breve descripción del título...",
    createButton: "Crear título",
    creating: "Creando...",
    titleRequired: "El título es obligatorio",
    createdSuccess: (title) => `"${title}" creado correctamente`,
    createError: "Error al crear el título",
  },

  // ── TMDB Result Card ──
  tmdb: {
    inDatabase: "En la base de datos",
    saveToDb: "Guardar",
    savedToDb: (title) => `"${title}" guardado en la base de datos`,
    saveError: "Error al guardar el título",
  },

  // ── Social / Profiles ──
  social: {
    followers: "Seguidores",
    following: "Siguiendo",
    follow: "Seguir",
    unfollow: "Dejar de seguir",
    followersOf: (name) => `Seguidores de ${name}`,
    followingOf: (name) => `${name} sigue a`,
    noFollowers: "Aún no tiene seguidores",
    noFollowing: "Aún no sigue a nadie",
    loadMore: "Cargar más",
    userNotFound: "Usuario no encontrado",
    username: "Nombre de usuario",
    usernamePlaceholder: "tu_nombre_de_usuario",
    usernameUpdated: "Nombre de usuario actualizado",
    usernameError: "Error al actualizar el nombre de usuario",
    viewProfile: "Ver perfil",
    editProfile: "Editar perfil",
    memberSince: "Miembro desde",
    publicLibrary: "Biblioteca pública",
  },

  // ── Activity Feed ──
  feed: {
    title: "Actividad",
    subtitle: "Lo último de la gente que sigues",
    empty: "Aún no hay actividad",
    emptyDesc: "Sigue a otros usuarios para ver su actividad aquí",
    findPeople: "Buscar personas",
    tabFeed: "Aventuras de tus amigos",
    tabRecommendations: "Aventuras recomendadas",
    recommendedYou: "te ha recomendado",
    aTitle: "un título",
    noRecommendations: "Sin recomendaciones",
    noRecommendationsDesc: "Cuando alguien te recomiende algo, aparecerá aquí.",
    actions: {
      "quiere ver": "quiere ver",
      "está viendo": "está viendo",
      "completó": "completó",
      "abandonó": "abandonó",
      "calificó": "calificó",
      "agregó": "agregó",
      "actualizó": "actualizó",
    },
    ratedWith: (n) => `con ${n}/5`,
    timeAgo: {
      now: "ahora",
      minutes: (n) => `hace ${n}m`,
      hours: (n) => `hace ${n}h`,
      days: (n) => `hace ${n}d`,
    },
  },

  // ── User Search ──
  userSearch: {
    title: "Buscar personas",
    placeholder: "Buscar por nombre o usuario...",
    noResults: "No se encontraron usuarios",
    searching: "Buscando...",
  },

  // ── Notifications ──
  notifications: {
    title: "Notificaciones",
    empty: "No tienes notificaciones",
    markAllRead: "Marcar todo como leído",
    newFollower: "empezó a seguirte",
  },

  // ── Share Modal ──
  share: {
    title: "Compartir",
    copyLink: "Copiar enlace",
    linkCopied: "Enlace copiado",
    createImage: "Crear imagen para compartir",
    recommendFriend: "Recomendar a un amigo",
    searchUser: "Buscar usuario...",
    noUsersFound: "No se encontraron usuarios",
    notFollowingAnyone: "Aún no sigues a nadie",
    recommendationSent: "Recomendación enviada",
    imageTitle: "Imagen para compartir",
    downloadImage: "Descargar imagen",
    downloadHelper: "Descarga la imagen y compártela en Instagram",
    imageForShare: "Imagen para compartir",
  },

  // ── Import Modal ──
  importModal: {
    title: "Importa tu biblioteca",
    description: "Sube un archivo exportado desde otras plataformas (Goodreads, Letterboxd, IMDb, Backloggd, Trakt)",
    dragHere: "Arrastra tu archivo aquí",
    clickToSelect: "o haz clic para seleccionar",
    importButton: "Importar biblioteca",
    importing: "Importando...",
    errorTitle: "Error al importar",
    tryAgain: "Intentar de nuevo",
    imported: (n) => `Has importado ${n} título${n !== 1 ? "s" : ""}`,
    fromPlatform: "desde",
    importedCount: "Importados",
    duplicates: "Duplicados",
    failed: "Fallidos",
    achievementsUnlocked: (n) => `Has desbloqueado ${n} logro${n !== 1 ? "s" : ""} nuevo${n !== 1 ? "s" : ""}`,
    goToAchievements: "Ve a Logros para verlos",
    done: "Listo",
  },

  // ── Social Share Card ──
  shareCard: {
    started: (title) => `Ha empezado ${title}`,
    completed: (title) => `Ha terminado ${title}`,
    planned: (title) => `Ha añadido a mi radar ${title}`,
    achievement: "Ha desbloqueado un logro",
  },

  // ── Achievements (extra keys) ──
  achievementsExtra: {
    overallProgress: "Progreso general",
    achievementsUnlocked: "logros desbloqueados",
    yourStreak: "Tu racha",
    consecutiveDays: "días seguidos",
    totalDays: "días totales",
    stageCompleted: "Etapa completada",
    remainingAchievements: (n) => `Te quedan ${n} logro${n !== 1 ? "s" : ""} para completar esta etapa.`,
    hiddenAchievements: "Logros ocultos que se revelan al completarlos.",
    shareOnInstagram: "Compartir en Instagram",
  },

  // ── Media Detail (extra keys) ──
  mediaDetailExtra: {
    share: "Compartir",
    reviews: "Reseñas",
    communityStatus: "Cómo lo está viviendo la comunidad",
    communityTabCompleted: "Terminados",
    communityTabInProgress: "Siguiendo ahora",
    communityTabPlanned: "En su radar",
    communityEmpty: "Aún no hay usuarios en esta categoría.",
  },

  // ── Achievement stage name translations ──
  achievementStages: {
    "Inicio": "Inicio",
    "Exploración": "Exploración",
    "Colección": "Colección",
    "Comunidad": "Comunidad",
    "Constancia": "Constancia",
    "Maestría": "Maestría",
    "Logros Secretos": "Logros Secretos",
  },

  // ── Language ──
  language: {
    label: "Idioma",
    es: "Español",
    en: "English",
  },
};

export default es;
